/**
 * Where a campaign stands in time, in a creator's words: "5 days left", "Starts in 2 days".
 */

import { formatDuration } from "../format.js";
import type { PublicCampaign } from "./campaigns.js";

/** A short line on the campaign's timing at `now`, adding to its phase badge. */
export function timingLine(
  campaign: Pick<PublicCampaign, "phase" | "startAt" | "endAt">,
  now = Date.now(),
): string {
  switch (campaign.phase) {
    case "upcoming":
      return `Starts in ${formatDuration(new Date(campaign.startAt).getTime() - now)}`;
    case "running":
      return `${formatDuration(new Date(campaign.endAt).getTime() - now)} left`;
    case "ended":
      return `Ended ${formatDuration(now - new Date(campaign.endAt).getTime())} ago`;
    case "settled":
      return "Payouts can be claimed";
    case "refunded":
      return "Budget returned to the brand";
  }
}

/** The most the creators of a campaign share: 80% of the budget, reached at the target. */
export function creatorsPool(campaign: Pick<PublicCampaign, "budget">): string {
  // Budgets are multiples of 5 base units, so this is exact.
  return ((BigInt(campaign.budget) * 4n) / 5n).toString();
}

/** How much of its window a campaign has run at `now`: 0 before the start, 1 from the end. */
export function elapsedShare(
  campaign: Pick<PublicCampaign, "startAt" | "endAt">,
  now = Date.now(),
): number {
  const start = new Date(campaign.startAt).getTime();
  const end = new Date(campaign.endAt).getTime();
  return Math.min(1, Math.max(0, (now - start) / (end - start)));
}
